import {BaseElement, BASIS, OSkinableInit} from "/@back@/commons/basis.js";
import {JLDTabs, OTabInit, OTabsInit, Tab, Tabs} from "/@back@/commons/widgets/tabs.js";
import {IArea} from "/@lib@/commons/areas.js";
import {ILastDatasBuilder, JLastDatas, LASTDATAS, OLastDatasInit} from "/@lib@/commons/lastDatas.js";
import {REG} from "/@lib@/commons/registry.js";
import {IView, IViewsContainer, OViewVisitOptions, VIEWS} from "/@lib@/commons/views.js";
import {DOM, JSX, ShadowJsx} from "/@lib@/commons/xml/dom.js";
import {DOMSH} from "/@lib@/commons/xml/domsh.js";
import {Resizer} from "/@back@/commons/widgets/resizer.js";
import {Button} from "/@back@/commons/widgets/buttons.js";
import {POPUP} from "/@back@/commons/widgets/popups.js";
import {IContextMenuActions, IContextMenuActionsPointer} from "/@back@/commons/actionables.js";
import {Action} from "/@lib@/commons/actions.js";
import {ContextMenuDeskFeat} from "/@back@/core/appFrame.js";

/**
 *
 * Attributs:
 * Config init att: toolbars-prefix
 * Config att: last-datas : key des lastDatas pour l'init et le build
 */
export interface Workbench<C> extends BaseElement {
	initialize(init?: OWorkbenchInit<C>): this;
}

export interface OWorkbenchInit<C = {}> extends OSkinableInit, OLastDatasInit {
	ctx?: C;

	/**
	 * Préfixe des listes d'areas constituant les toolbars.
	 * Exemple : "myApp:" définit les listes "myApp:tl", "myApp:tr", "myApp:lt", "myApp:lt", "myApp:rt", "myApp:rb", "myApp:bl", "myApp:br"
	 * DOIT être spécifié.
	 */
	toolbarsPrefix?: string

	lt?: OWbToolsInit<C>
	lb?: OWbToolsInit<C>
	bl?: OWbToolsInit<C>
	br?: OWbToolsInit<C>
	rt?: OWbToolsInit<C>
	rb?: OWbToolsInit<C>
}

export interface IWbToolArea<C = {}, E extends HTMLElement = HTMLElement> extends IArea<C, E> {

	/** Interdit d'insérer cet Area dans un volet orienté horizontalement ou verticalement. */
	forbidInLayout?: 'h' | 'v'
}

/** Wrap d'un IWbToolArea avec des indications sur le context graphique. */
export interface IWbToolLocatedArea<C = {}, E extends HTMLElement = HTMLElement> extends IWbToolArea<C, E> {
	/** area wrappée non localisée. */
	unlocatedArea: IWbToolArea<C, E>
	layout: 'h' | 'v'
}

/**
 * Vocabulaire :
 * - flap : volets gauche, droit et bas
 * - pan : panneau, zone d'affichage du contenu des views
 * - tabs : jeu d'onglets pour sélectionner le panneau avec le bouton de réduction de ces tabs et/ou de ces tabs.
 * - pans : regroupement du pan et de son tabs (pour le flap bas)
 */
export class Workbench<C> extends BaseElement implements IViewsContainer, ILastDatasBuilder {

	get tools(): NodeListOf<WbTools<C>> {return this.shadowRoot.querySelectorAll('c-wb-tools')}

	getAreaTools(): IArea<C>[] {
		const r = [] as IArea<C>[];
		for (const tool of this.tools) r.push(...tool.areas);
		return r;
	}

	showAreaTool(areaId: string): Promise<boolean> | void {
		for (const tool of this.tools) {
			const tab = tool.getTabByCode(areaId);
			if (tab) return tool.selectTab(tab);
		}
	}

	toggleAreaTool(areaId: string): Promise<boolean> | void {
		for (const tool of this.tools) {
			const tab = tool.getTabByCode(areaId);
			if (tab) return tab.selected ? tool.unselectTab() : tool.selectTab(tab);
		}
	}


	getTab(areaId: string): Tab<C> | null {
		for (const tool of this.tools) {
			const tab = tool.getTabByCode(areaId);
			if (tab) return tab;
		}
		return null;
	}

	protected _initialize(init: OWorkbenchInit<C>) {
		const reg = this.findReg(init);
		const sr = this.attachShadow(DOMSH.SHADOWDOM_INIT);
		reg.installSkin('webzone:panel', sr);
		this._initAndInstallSkin(this.localName, init);

		const areasContext = init.ctx ? init.ctx : this;
		const tbPrefix = init.toolbarsPrefix;
		const lastDatas = init.lastDatas as JLDWorkbench;

		const ltT: IWbToolArea[] = reg.getList(tbPrefix + 'lt') || [];
		const lbT: IWbToolArea[] = reg.getList(tbPrefix + 'lb') || [];
		const blT: IWbToolArea[] = reg.getList(tbPrefix + 'bl') || [];
		const brT: IWbToolArea[] = reg.getList(tbPrefix + 'br') || [];
		const rtT: IWbToolArea[] = reg.getList(tbPrefix + 'rt') || [];
		const rbT: IWbToolArea[] = reg.getList(tbPrefix + 'rb') || [];

		const all = [ltT, lbT, blT, brT, rtT, rbT];

		//Replacement des tools par les userPrefs
		for (let k = 0, s=all.length; k<s; k++) {
			const l = all[k];
			for (let i = l.length - 1; i >= 0; i--) {
				const a = l[i];
				if (a) {
					const pos = reg.getUserData(tbPrefix + a.getId());
					if(pos && pos !== allPos[k]) {
						//On déplace a vers pos
						switch(pos){
						case 'lt': ltT.push(a); break;
						case 'lb': lbT.push(a); break;
						case 'bl': blT.push(a); break;
						case 'br': brT.push(a); break;
						case 'rt': rtT.push(a); break;
						case 'rb': rbT.push(a); break;
						default: continue; //pos pas trouvé, on passe.
						}
						l.splice(i, 1);
					}
				}
			}
		}

		if (lastDatas) {
			//Replacement des tools par les lastDatas
			function removeAndGet(area: string, layout: 'h' | 'v'): IWbToolArea | null {
				for (const l of all) {
					for (let i = l.length - 1; i >= 0; i--) {
						const a = l[i];
						if (a?.getId() === area && a.forbidInLayout !== layout) {
							//Area trouvé et target layout compatible.
							l.splice(i, 1);
							return a;
						}
					}
				}
				return null;
			}

			function moveTo(areas: string[], target: IWbToolArea[], layout: 'h' | 'v') {
				if (!areas) return;
				for (const aId of areas) {
					const a = removeAndGet(aId, layout);
					if (a) target.push(a);
				}
			}

			moveTo(lastDatas.ltT, ltT, 'v');
			moveTo(lastDatas.lbT, lbT, 'v');
			moveTo(lastDatas.rtT, rtT, 'v');
			moveTo(lastDatas.rbT, rbT, 'v');
			moveTo(lastDatas.blT, blT, 'h');
			moveTo(lastDatas.brT, brT, 'h');
		}

		sr.appendChild(
			<div id="mid" c-resizable="">
				<div id="flapL" c-resizable="">
					<did id="pansLT" class="pans" c-resizable="">
						<div id="tabsLT" class="tabs">
							<button id="closeLT" title="Refermer ce volet" onclick={this.onClosePan}/>
						</div>
						<WbTools î={Object.assign({orient: 'lt', areas: ltT, areasContext, lastDatas, workbench: this}, init.lt)}/>
					</did>
					<Resizer class="pansSep" c-orient="column"/>
					<did id="pansLB" class="pans" c-resizable="">
						<div id="tabsLB" class="tabs">
							<button id="closeLB" title="Refermer ce volet" onclick={this.onClosePan}/>
						</div>
						<WbTools î={Object.assign({orient: 'lb', areas: lbT, areasContext, lastDatas, workbench: this}, init.lb)}/>
					</did>
					<div id="bTabsL" class="bTabs"/>
				</div>
				<Resizer c-orient="row"/>
				<div id="main" c-resizable="">
					<slot/>
				</div>
				<Resizer c-orient="row"/>
				<div id="flapR" c-resizable="">
					<did id="pansRT" class="pans pansH" c-resizable="">
						<div id="tabsRT" class="tabs">
							<button id="closeRT" title="Refermer ce volet" onclick={this.onClosePan}/>
						</div>
						<WbTools î={Object.assign({orient: 'rt', areas: rtT, areasContext, lastDatas, workbench: this}, init.rt)}/>
					</did>
					<Resizer class="pansSep" c-orient="column"/>
					<did id="pansRB" class="pans pansH" c-resizable="">
						<div id="tabsRB" class="tabs">
							<button id="closeRB" title="Refermer ce volet" onclick={this.onClosePan}/>
						</div>
						<WbTools î={Object.assign({orient: 'rb', areas: rbT, areasContext, lastDatas, workbench: this}, init.rb)}/>
					</did>
					<div id="bTabsR" class="bTabs"/>
				</div>
			</div>
		);

		sr.appendChild(<Resizer class="sep" c-orient="column"/>);
		sr.appendChild(<div id="flapB" c-resizable="">
			<div id="pansBL" class="pans" c-resizable="">
				<div id="tabsBL" class="tabs">
					<button id="closeBL" title="Refermer ce volet" onclick={this.onClosePan}/>
				</div>
				<WbTools î={Object.assign({orient: 'bl', areas: blT, areasContext, lastDatas, workbench: this}, init.bl)}/>
			</div>
			<Resizer class="pansSep" c-orient="row"/>
			<div id="pansBR" class="pans" c-resizable="">
				<div id="tabsBR" class="tabs">
					<button id="closeBR" title="Refermer ce volet" onclick={this.onClosePan}/>
				</div>
				<WbTools î={Object.assign({orient: 'br', areas: brT, areasContext, lastDatas, workbench: this}, init.br)}/>
			</div>
		</div>);

		//LastDatas
		if (init.lastDatasKey) this.setAttribute("last-datas", init.lastDatasKey);
		if (lastDatas) {
			if (lastDatas.mw > 0) sr.getElementById("main").style.flexBasis = lastDatas.mw + "px";
			if (lastDatas.mh > 0) sr.getElementById("mid").style.flexBasis = lastDatas.mh + "px";
			if (lastDatas.lw > 0) sr.getElementById("flapL").style.flexBasis = lastDatas.lw + "px";
			if (lastDatas.rw > 0) sr.getElementById("flapR").style.flexBasis = lastDatas.rw + "px";
			if (lastDatas.bh > 0) sr.getElementById("flapB").style.flexBasis = lastDatas.bh + "px";
			if (lastDatas.lth) sr.getElementById("pansLT").style.flexBasis = lastDatas.lth;
			if (lastDatas.lbh) sr.getElementById("pansLB").style.flexBasis = lastDatas.lbh;
			if (lastDatas.blw) sr.getElementById("pansBL").style.flexBasis = lastDatas.blw;
			if (lastDatas.brw) sr.getElementById("pansBR").style.flexBasis = lastDatas.brw;
			if (lastDatas.rth) sr.getElementById("pansRT").style.flexBasis = lastDatas.rth;
			if (lastDatas.rbh) sr.getElementById("pansRB").style.flexBasis = lastDatas.rbh;
		}
		for (const tools of this.tools) tools.injectTabs();
		for (const tools of this.tools) tools.refreshSelectedTab();
	}

	buildInitFromAtts(init?: OWorkbenchInit<C>): OWorkbenchInit<C> {
		init = super.buildInitFromAtts(init);
		init.toolbarsPrefix = this.getAttribute("toolbars-prefix");
		init.lastDatas = LASTDATAS.getLastDatas(this, this.getAttribute("last-datas"));
		return init;
	}

	buildLastDatas(p: JLastDatas) {
		const k = this.getAttribute("last-datas");
		if (k) {
			const d = p[k] = {} as JLDWorkbench;
			const sr = this.shadowRoot;
			d.mw = sr.getElementById("main").offsetWidth;
			d.mh = sr.getElementById("mid").offsetHeight;
			d.lw = sr.getElementById("flapL").offsetWidth;
			d.rw = sr.getElementById("flapR").offsetWidth;
			d.bh = sr.getElementById("flapB").offsetHeight;
			d.lth = sr.getElementById("pansLT").style.flexBasis;
			d.lbh = sr.getElementById("pansLB").style.flexBasis;
			d.blw = sr.getElementById("pansBL").style.flexBasis;
			d.brw = sr.getElementById("pansBR").style.flexBasis;
			d.rth = sr.getElementById("pansRT").style.flexBasis;
			d.rbh = sr.getElementById("pansRB").style.flexBasis;
			for (const tools of this.tools) {
				if (tools.forcedTools?.length > 0) (d as any)[tools.id + "T"] = tools.forcedTools;
				tools.buildLastDatas(d);
				//else d[tools.id as 'lt' | 'lb' | 'rt' | 'rb' | 'bl' | 'br'] = null
			}
		}
		//on poursuit dans le contenu principal
		LASTDATAS.buildLastDatas(p, this);
	}

	visitViews(visitor: (view: IView) => any, options?: OViewVisitOptions) {
		for (const tools of this.tools) {
			if (!options || !options.visible || !tools.closest('[hidden]')) {
				const r = visitor(tools);
				if (r !== undefined) return r;
			}
		}
	}

	async visitViewsAsync(visitor: (view: IView) => Promise<any>, options?: OViewVisitOptions): Promise<any> {
		for (const tools of this.tools) {
			if (!options || !options.visible || !tools.closest('[hidden]')) {
				const r = await tools.visitViewsAsync(visitor, options);
				if (r !== undefined) return r;
			}
		}
	}

	findTabView(codeView: string): Tab {
		for (const tools of this.tools) {
			const tab = tools.getTabByCode(codeView);
			if (tab) return tab;
		}
		return null;
	}

	async showView(codeView: string): Promise<IView> {
		const tab = this.findTabView(codeView);
		if (tab && await tab.parentViewCtn.selectTab(tab)) return tab.view;
		return null;
	}

	onClosePan(this: Button) {
		(this.parentElement.nextElementSibling as WbTools<C>).unselectTab();
	}
}

interface JLDWorkbench {
	//Tailles
	mw?: number
	mh?: number
	lw?: number
	rw?: number
	th?: number
	bh?: number
	lth?: string
	lbh?: string
	blw?: string
	brw?: string
	rth?: string
	rbh?: string
	//Déplacements
	ltT?: string[]
	lbT?: string[]
	blT?: string[]
	brT?: string[]
	rtT?: string[]
	rbT?: string[]
	//SubLastDatas
	main?: JLastDatas
	lt?: JLastDatas
	lb?: JLastDatas
	rt?: JLastDatas
	rb?: JLastDatas
	bl?: JLastDatas
	br?: JLastDatas
}

REG.reg.registerSkin('c-workbench', 1, /* language=CSS */ `
	:host {
		display: flex;
		min-height: 0;
		min-width: 0;
		flex-direction: column;
		flex: 1;
		padding: 5px;
		background-color: inherit; /*contre webzone:panel*/
	}

	#mid {
		flex: 5 5 60vh;
		display: flex;
		min-height: 10%;
		min-width: 0;
		overflow: hidden;
	}

	#main {
		flex: 5 5 60vw;
		display: flex;
		min-height: 0;
		min-width: 10%;
		flex-direction: column;
		overflow: auto;
		border: 1px solid var(--page-border-color);
		border-radius: 7px;
	}

	[hidden] {
		display: none !important;
	}

	#flapB {
		flex: 1 1 20vh;
		display: flex;
		min-width: 0;
		min-height: 5em;
		flex-direction: row;
	}

	#flapL,
	#flapR {
		flex: 1 1 20vw;
		display: flex;
		min-width: 5em;
		min-height: 0;
		flex-direction: column;
	}

	#flapL.closed,
	#flapR.closed,
	#flapB.closed {
		flex: 0 0 auto;
		min-width: 0;
		min-height: 0;
	}

	:host(.hideFlapTabs) > * > #flapL.closed,
	:host(.hideFlapTabs) > * > #flapR.closed,
	:host(.hideFlapTabs) > #flapB.closed,
	:host(.hideFlapTabs) .pans[pan-state='collapsed'],
	:host(.hideFlapTabs) .tabs.collapsed,
	:host(.hideFlapTabs) .pansSep[disabled] {
		display: none !important;
	}

	.panV {
		flex: 1 1 15vh;
		display: flex;
		min-width: 3em;
		min-height: 0;
		flex-direction: column;
	}

	.panH {
		display: flex;
		min-height: 3em;
		min-width: 0;
		flex-direction: row;
	}

	.pans {
		flex: 1 1 50%;
		display: flex;
		min-width: 0;
		min-height: 0;
		flex-direction: column;
		border: 1px solid var(--border-color);
		border-radius: 7px;
		overflow: hidden;
	}

	.pans[pan-state='closed'],
	.pansH[pan-state='collapsed'] {
		flex: 1 1 50%;
		border: none;
		box-shadow: unset;
	}

	[pan-state='closed'] > .tabs,
	.bTabs > .tabs,
	.pansH[pan-state='collapsed'] > .tabs {
		border: 1px solid var(--border-color);
	}

	.pans[pan-state='collapsed'] {
		flex: 0 0 auto;
	}

	.bTabs > .tabs {
		margin-top: 5px;
	}

	#pansBL[pan-state='collapsed'],
	#pansBR[pan-state='collapsed'] {
		align-self: flex-end;
	}

	.tabs.collapsed {
		border-radius: 7px;
	}

	c-wb-tools {
		flex: 1;
		border-top: 1px solid var(--border-color);
		background-color: var(--bgcolor);
	}

	c-resizer {
		background-color: transparent;
	}

	c-resizer[c-orient=row] {
		width: 5px;
	}

	c-resizer[c-orient=column] {
		height: 5px;
	}

	.tabs {
		background-color: var(--page-bgcolor);
	}

	#tabsLT, #tabsLB, #tabsBL {
		display: flex;
		flex-direction: row;
		align-items: center;
	}

	#tabsRT, #tabsRB, #tabsBR {
		display: flex;
		flex-direction: row-reverse;
		align-items: center;
	}

	c-oriented-box {
		flex: 1;
		display: flex;
		min-height: 0;
		min-width: 0;
	}

	c-oriented-box[vertical] {
		flex: 1 1 auto;
		flex-direction: column;
	}

	c-wb-tooltab {
		flex: 1 1 auto;
		display: flex;
		min-height: 0;
		min-width: 0;
		overflow: hidden;
		align-items: center;
		/*justify-content: center;*/
		padding: .2em;
		cursor: pointer;
		user-select: none;
	}

	:not([vertical]) > c-wb-tooltab:not(:first-child) {
		border-inline-start: 1px solid var(--border-color);
	}

	[vertical] > c-wb-tooltab:not(:first-child) {
		border-block-start: 1px solid var(--border-color);
	}

	:not(.collapsed) > * > c-wb-tooltab {
		background: var(--item-bgcolor);
	}


	c-wb-tooltab:hover, c-wb-tooltab.willBeShown {
		background: var(--pressed-bgcolor);
	}

	c-wb-tooltab.moved {
		z-index: 1;
	}

	c-wb-tooltab.selected {
		/*z-index: 1;*/
		background: var(--page-bgcolor);
		cursor: default;
	}

	c-wb-tooltab:not(.moved) {
		transition: top 0.2s linear, left 0.2s linear;
	}

	c-wb-tooltab:focus {
		outline: var(--focus-outline);
		outline-offset: -2px;
	}

	c-wb-tooltab > .icon {
	  height: var(--icon-size);
	  width: var(--icon-size);
	  min-height: var(--icon-size);
	  min-width: var(--icon-size);
	  background: var(--icon-img) no-repeat center / contain;
	  filter: var(--filter);
	  margin: 1px;
  }

  c-wb-tooltab > .label {
	  overflow: hidden;
	  white-space: nowrap;
	  text-overflow: ellipsis;
  }

  /*[pan-state="closed"] > * > * > c-wb-tooltab > .label {display: none;}*/
  [vertical] > c-wb-tooltab > .label {
	  display: none;
  }

  button {
	  appearance: none;
	  background: no-repeat center / contain;
		border: 3px solid transparent;
		align-self: stretch;
		cursor: pointer;
		filter: var(--filter);
	}

	button:hover {
		background-color: var(--pressed-bgcolor);
	}

	button:focus {
		outline: var(--focus-outline);
		outline-offset: -2px;
	}

	.collapsed > button {
		display: none;
	}

	#tabsLT > button,
	#tabsLB > button,
	#tabsBL > button.sibling {
		background-image: url('/@skin@/core/widgets/workbench/closeToolL.svg');
		min-width: var(--icon-size);
		min-height: var(--icon-size);
	}

	#tabsRT > button,
	#tabsRB > button,
	#tabsBR > button.sibling {
		background-image: url('/@skin@/core/widgets/workbench/closeToolR.svg');
		min-width: var(--icon-size);
		min-height: var(--icon-size);
	}

	#tabsLT > button.sibling,
	#tabsRT > button.sibling {
		background-image: url('/@skin@/core/widgets/workbench/closeToolT.svg');
		min-width: var(--icon-size);
		min-height: var(--icon-size);
	}

	#tabsBL > button,
	#tabsBR > button,
	#tabsLB > button.sibling,
	#tabsRB > button.sibling {
		background-image: url('/@skin@/core/widgets/workbench/closeToolB.svg');
		min-width: var(--icon-size);
		min-height: var(--icon-size);
	}

	:host(.hideFlapTabs) .tabs > button {
		background-image: url('/@skin@/core/widgets/workbench/closeTool.svg') !important;
		min-width: var(--icon-size);
		min-height: var(--icon-size);
	}
`);


customElements.define('c-workbench', Workbench);

interface OWbToolsInit<C> extends OTabsInit<C> {
	orient?: 'lt' | 'lb' | 'rt' | 'rb' | 'bl' | 'br'
	vertical?: never
	lastDatas?: JLDWorkbench | JLDTabs //init via JLDWorkbench résolu en JLDTabs
	workbench?: Workbench<C>
}

class WbTools<C> extends Tabs<C> {

	forcedTools?: string[]

	/** Ce pan est-il vertical (lt, lb, rt, rb) ou horizontal (bl, br) ? */
	isVerticalPan: boolean

	/** Ce pan est-il le premier dans le flap (lt, rt, bk) */
	isFirstPan: boolean

	/** Ce pan est-il à droite (rt, rb, br) */
	isRightPan: boolean

	tabsBar: HTMLDivElement;

	get flapClosed(): boolean {return this.classList.contains("flapClosed")}

	protected async _initialize(init: OWbToolsInit<C>): Promise<void> {
		init.noArrowScroll = true;
		this.id = init.orient;
		const firstPos = this.id[0];
		const secondPos = this.id[1];
		this.isVerticalPan = firstPos !== 'b';
		this.isFirstPan = secondPos === 't' || secondPos === 'l';
		this.isRightPan = this.isVerticalPan ? firstPos === 'r' : secondPos === 'r';

		this.classList.add(this.isVerticalPan ? "panV" : "panH");
		if (init.lastDatas) {
			this.forcedTools = (init.lastDatas as any)[init.orient + "T"] as string[];
			init.lastDatas = (init.lastDatas as JLDWorkbench)[init.orient] as JLDTabs;
		}
		this.setAttribute("last-datas", init.orient);
		await super._initialize(init);
		if (!this._areas) this._areas = [];
	}

	protected _initSkins(init: OTabsInit<C>) {
	}

	/** Finalise l'init avec le placement des tabs pour ce pan. */
	injectTabs() {
		this.tabsBar = this.previousElementSibling as HTMLDivElement;
		this.tabsBar.insertAdjacentElement("afterbegin", this._tabs);
	}

	_initShadowRoot() {
		this.shadowRoot.appendChild(this._mainSlot);
	}


	_initTabs() {
		this._areas?.forEach((area: IArea<C>) => {this._newTab(area)});
	}

	async selectTab(tab: Tab<C>, lastDatas?: JLastDatas): Promise<boolean> {
		if (tab == null) return this.unselectTab();
		const res = await super.selectTab(tab, lastDatas);
		this.refreshSelectedTab();
		return res;
	}

	async unselectTab(): Promise<boolean> {
		if (!this._selectedTab) return false;
		if (!await this._selectedTab.hide()) return false;
		this._selectedTab = null;
		this.refreshSelectedTab();
		return true;
	}

	async closeTab(tab: Tab<C>, silently?: boolean, removeFromAreas?: boolean): Promise<boolean> {
		const ok = await super.closeTab(tab, silently, removeFromAreas);
		if (ok) this.refreshSelectedTab();
		return ok;
	}

	async addTab(area: IArea<C>, options?: { select?: boolean; position?: number; view?: IView }): Promise<Tab<C>> {
		const tab = await super.addTab(area, options);
		this.refreshSelectedTab();
		return tab;
	}

	_newTab(area: IArea<C>, before?: Tab<C>, view?: IView): Tab<C> {
		const tab = new WbToolTab<C>().initialize({
			parentViewCtn: this,
			area,
			view
		});
		tab.onclick = function (this: WbToolTab<C>) {
			this.parentViewCtn.selectTab(this);
		};
		tab.showOnDragOver = true;
		this._tabs.insertBefore(tab, before);
		return tab;
	}

	refreshSelectedTab() {
		if (DOM.setHidden(this.getPans(), !this.hasTabs())) {
			DOM.setHidden(this.getPanResizer(), !this.hasTabs())
		}

		const siblingTools = this.getSiblingTools();
		const flapClosed: boolean = !this._selectedTab && !siblingTools.selectedTab;
		const panCollapsed = !this._selectedTab;
		const panSiblingCollapsed = !siblingTools.selectedTab;

		this.getFlapResizer().disabled = flapClosed;
		this.setFlapClosed(flapClosed, panCollapsed, panSiblingCollapsed);
		siblingTools.setFlapClosed(flapClosed, panSiblingCollapsed, panCollapsed);
		this.getFlap().classList.toggle("closed", flapClosed);
		if (flapClosed) this.getFlap().style.flex = ""; //reset Resizer
		this.tabsBar.classList.toggle("collapsed", panCollapsed);
		if (DOM.setHidden(this, panCollapsed) && panCollapsed) {
			VIEWS.onViewShown(this);
			this.getPans().style.flex = ""; //reset Resizer
		}
		this.getPanResizer().disabled = panCollapsed || panSiblingCollapsed;

		if (!panCollapsed) this.getTabsCloseBtn().classList.toggle("sibling", !panSiblingCollapsed);
		if (!panSiblingCollapsed) siblingTools.getTabsCloseBtn().classList.toggle("sibling", !panCollapsed);

		this.refresh();
		siblingTools.refresh();
	}

	/** Ajustement du pan et de ses tabs en fonction de l'état ouvert/fermé de son flap */
	protected setFlapClosed(closed: boolean, panCollapsed: boolean, panSiblingCollapsed: boolean) {
		this.classList.toggle("flapClosed", closed);
		if (!this.isVerticalPan) {
			//flap bas, on place les tabs dans le bon container
			const tabs = this.tabsBar;
			if (closed) {
				if (tabs.parentElement === this.parentElement) this.getTabsBarClosed().appendChild(tabs);
			} else {
				if (tabs.parentElement !== this.parentElement) this.insertAdjacentElement("beforebegin", tabs);
			}
		}
		if (!this.isVerticalPan) {
			//Flap bas
			DOM.setAttrBool(this, 'vertical', panCollapsed && (this.isLateralFlapCollapsed() || !panSiblingCollapsed));
		} else {
			//Flap latéral,
			DOM.setAttrBool(this, 'vertical', closed);
			//on ajuste notre flap bas
			const bottomTools = this.getBottomTools();
			if (DOM.setAttrBool(bottomTools, 'vertical', !bottomTools.selectedTab && (panCollapsed && panSiblingCollapsed || !!bottomTools.getSiblingTools().selectedTab))) bottomTools.refresh();
		}

		if (closed) this.getPans().style.flex = ""; //reset Resizer
		DOM.setAttr(this.getPans(), "pan-state", closed ? "closed" : !this._selectedTab ? "collapsed" : "opened");
	}

	getPans(): HTMLElement {
		return this.parentElement;
	}

	getFlap(): HTMLElement {
		return this.parentElement.parentElement;
	}

	getFlapResizer(): Resizer {
		const flap = this.getFlap();
		if (flap.id === "flapL") return flap.nextElementSibling as Resizer;
		return flap.previousElementSibling as Resizer; //flapR et flapB
	}

	getPanResizer(): Resizer {
		//if (this.isVerticalPan) return (this.isFirstPan ? this.nextElementSibling : this.previousElementSibling.previousElementSibling) as Resizer;
		return (this.isFirstPan ? this.parentElement.nextElementSibling : this.parentElement.previousElementSibling) as Resizer;
	}

	getTabsCloseBtn(): HTMLButtonElement {
		return this.tabsBar.lastElementChild as HTMLButtonElement;
	}

	/** Onglets refermés des pans en bas */
	getTabsBarClosed(): HTMLDivElement {
		return DOMSH.findDocumentOrShadowRoot(this).getElementById(this.isRightPan ? "bTabsR" : "bTabsL") as HTMLDivElement;
	}

	getSiblingTools(): WbTools<C> {
		//if (this.isVerticalPan) return (this.isFirstPan ? this.nextElementSibling.nextElementSibling.nextElementSibling : this.previousElementSibling.previousElementSibling.previousElementSibling) as WbTools<C>;
		return (this.isFirstPan ? this.parentElement.nextElementSibling.nextElementSibling.lastElementChild : this.parentElement.previousElementSibling.previousElementSibling.lastElementChild) as WbTools<C>;
	}

	getBottomTools(): WbTools<C> {
		return DOMSH.findDocumentOrShadowRoot(this).getElementById(this.isRightPan ? "br" : "bl") as WbTools<C>;
	}

	isLateralFlapCollapsed() {
		return DOMSH.findDocumentOrShadowRoot(this).getElementById(this.isRightPan ? "flapR" : "flapL").classList.contains("closed");
	}

	onMoveTab(tab: WbToolTab<C>, ev: Event) {
		const me = this;

		async function onSelPan(this: HTMLButtonElement, ev: Event) {
			POPUP.findPopupableParent(this).close();
			const area = tab.area;
			const target = DOMSH.findDocumentOrShadowRoot(me).getElementById(this.id) as WbTools<C>;
			await me.unselectTab();
			if (await me.closeTab(tab, false, true)) {
				const idx = me.forcedTools?.indexOf(area.getId());
				if (idx >= 0) me.forcedTools.splice(idx, 1);
				(target.forcedTools || (target.forcedTools = [])).push(area.getId());
				await target.addTab(area, {select: true});
			}
		}

		function makeBtn(id: string, layout: 'h' | 'v') {
			return <button id={id} onclick={onSelPan} class={me.id === id ? 'current' : undefined} disabled={me.id === id || (tab.area as IWbToolArea).forbidInLayout === layout ? "" : undefined} style={"grid-area:" + id}/>
		}

		const body = <div><ShadowJsx skin="c-wb-tools/movePopup">
			{makeBtn("lt", 'v')}
			{makeBtn("lb", 'v')}
			<div id="central">Sélectionnez un des volets disponibles</div>
			{makeBtn("rt", 'v')}
			{makeBtn("rb", 'v')}
			{makeBtn("bl", 'h')}
			{makeBtn("br", 'h')}
		</ShadowJsx></div>;
		const isBottom = me.id.charAt(0) === 'b';
		POPUP.showFloating(body, {posFrom: ev.target as HTMLElement, fromY: isBottom ? "top" : "bottom", targetY: isBottom ? "bottom" : "top", toleranceX: 500}, this, {closeOnBlur: true});
	}

	_onClose(this: Button, ev: Event) {
		DOMSH.findHost<WbTools<C>>(this).unselectTab();
	}
}

REG.reg.registerSkin('c-wb-tools/movePopup', 1, /* language=CSS */ `
	:host {
		display: grid;
		grid-template: 
			"lt m  m  rt" 2fr
			"lb m  m  rb" 2fr
			"bl bl br br" 1fr / 1fr 1fr 1fr 1fr;
		width: 12em;
		height: 12em;
		background-color: var(--border-color);
	}

	#central {
		grid-area: m;
		display: flex;
		align-items: center;
		justify-content: center;
		text-align: center;
	}

	button {
		background-color: var(--panel-bgcolor);
		border: 1px solid var(--panel-border-color);
		cursor: pointer;
	}

	.current {
		background-color: var(--panel-row-inSel-bgcolor) !important;
	}

	button:not([disabled]):hover {
		background-color: var(--panel-tabf-bgcolor);
	}

	button[disabled] {
		background-color: #88888855;
		cursor: auto;
	}

	button[disabled]::before {
		content: '🛇';
		font-size: .8rem;
		color: var(--panel-color);
	}

	.v {
		min-width: 1em;
		min-height: 2em;
	}

	.h {
		min-width: 2em;
		min-height: 1em;
	}
`);

customElements.define('c-wb-tools', WbTools);

class WbToolTab<C> extends Tab<C> implements IContextMenuActionsPointer<WbToolTab<C>> {

	parentViewCtn: WbTools<C>;

	ctxMenuActions: IContextMenuActions<WbToolTab<C>> = {
		actions: [
			new Action("openClose").setLabel((ctx: WbToolTab<C>) => ctx._shown ? "Refermer ce volet" : "Ouvrir ce volet")
				.setExecute((ctx: WbToolTab<C>, ev) => {
					if (ctx._shown) ctx.parentViewCtn.unselectTab();
					else ctx.parentViewCtn.selectTab(ctx);
				}),
			new Action("move").setLabel("Déplacer ce volet...").setExecute((ctx: WbToolTab<C>, ev) => {
				ctx.parentViewCtn.onMoveTab(ctx, ev);
			})
		], actionContext: this
	};

	protected _initialize(init: OTabInit<C>) {
		//init config
		this.parentViewCtn = init.parentViewCtn as WbTools<C>;
		this.view = init.view;
		this.area = init.area;
		//init en dur de OButtonConfig
		this.setAttribute("role", "button"); //XXX aria role dédié pour tab ?
		BASIS.makeClickable(this);
		//pas de shadowDom
		this._createIcon();
		this.ondblclick = (ev) => {
			if (ContextMenuDeskFeat.isIn(desk)) desk.showContextMenu.call(document.body, ev);
		}
	}

	async show(lastDatas?: JLastDatas): Promise<void> {
		const layout = (this.parentViewCtn as WbTools<any>).isVerticalPan ? "v" : "h";
		if ((this.area as IWbToolLocatedArea).layout !== layout) {
			//Wrap this.area avec les info de layout.
			const unlocated = (this.area as IWbToolLocatedArea).unlocatedArea || this.area;
			const located = Object.create(unlocated) as IWbToolLocatedArea;
			located.layout = layout;
			located.unlocatedArea = unlocated;
			this.area = located;
		}
		await super.show(lastDatas);
	}

	protected _refresh() {
		super._refresh();
		DOM.setAttr(this._iconElt, "title", VIEWS.getDescription(this.view || this) || VIEWS.getLabel(this.view || this)); //pour le mode collapsé.
	}
}

customElements.define('c-wb-tooltab', WbToolTab);

const allPos = ["lt", "lb", "bl", "br", "rt", "rb"];